import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Calculator } from "lucide-react";
import { useDealContext } from "../hooks/useDealContext";
import { fetchScenarios, updateAndRunScenario, type ScenarioResponse } from "../api/client";
import { updateDealAssumptions } from "../api/client";
import AssumptionsPanel, {
  type Assumptions,
  buildApiPayload,
  buildDealAssumptionsPatch,
} from "../components/AssumptionsPanel";
import EmptyState from "../components/EmptyState";
import LoadingSpinner from "../components/LoadingSpinner";
import { useToastContext } from "../contexts/ToastContext";
import { friendlyError } from "../utils/errors";
import { fmtCurrency, fmtPercent } from "../utils/format";

export default function AssumptionsPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const { deal, setDeal, scenarios: ctxScenarios, setScenarios: setCtxScenarios } = useDealContext();
  const { addToast } = useToastContext();
  const [localScenarios, setLocalScenarios] = useState<ScenarioResponse[]>([]);
  const scenarios = ctxScenarios && ctxScenarios.length > 0 ? ctxScenarios : localScenarios;
  const setScenarios = (s: ScenarioResponse[]) => { setLocalScenarios(s); setCtxScenarios?.(s); };
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [recalculating, setRecalculating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [assumptions, setAssumptions] = useState<Assumptions>({
    vacancy_rate_pct: 0.05, expense_growth_rate_pct: 0.03, going_in_cap_rate: 0.065,
    exit_cap_rate: 0.065, discount_rate: 0.08, market_rent_growth_pct_annual: 0.02,
    capital_reserves_psf: 0.25, credit_loss_pct: 0.01, selling_costs_pct: 0.015,
    renewal_probability_pct: 0.70, downtime_months: 6, lease_term_years: 5,
    new_tenant_ti_psf: 20, renewal_tenant_ti_psf: 5, analysis_start_date: "",
    hold_period_years: 10, purchase_price: 0, loan_amount: 0,
    interest_rate: 0.05, amortization_years: 25, io_period_years: 0,
  });

  useEffect(() => {
    if (!dealId) return;
    setLoading(true);
    fetchScenarios(dealId).then((list) => {
      setScenarios(list);
      const active = list.find((s) => s.projection_result) ?? list[0];
      if (active) {
        setSelectedId(active.id);
        loadAssumptionsFromScenario(active);
      }
    }).catch((e: any) => {
      addToast(
        friendlyError(e?.response?.data?.detail || e?.message || "Couldn't load assumptions."),
        "error",
      );
    }).finally(() => setLoading(false));
  }, [dealId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAssumptionsFromScenario = (s: ScenarioResponse) => {
    const sa = s.assumptions || {};
    const pd = (deal?.property_data || {}) as Record<string, any>;
    const ra = (sa.rollover_assumptions || pd.rollover_assumptions || {}) as Record<string, unknown>;
    const debt = (sa.debt || pd.debt || {}) as Record<string, unknown>;

    setAssumptions({
      vacancy_rate_pct: (sa.vacancy_rate_pct ?? pd.general_vacancy_pct ?? 0.05) as number,
      expense_growth_rate_pct: (sa.expense_growth_rate_pct ?? pd.expense_growth_rate_pct ?? 0.03) as number,
      going_in_cap_rate: (pd.going_in_cap_rate ?? 0.065) as number,
      exit_cap_rate: (sa.exit_cap_rate ?? pd.exit_cap_rate ?? 0.065) as number,
      discount_rate: (sa.discount_rate ?? pd.discount_rate ?? 0.08) as number,
      market_rent_growth_pct_annual: (sa.market_rent_growth_pct_annual ?? (ra.market_rent_growth_pct_annual as number) ?? 0.02) as number,
      capital_reserves_psf: (sa.capital_reserves_psf ?? pd.capital_reserves_psf ?? 0.25) as number,
      credit_loss_pct: (sa.credit_loss_pct ?? pd.credit_loss_pct ?? 0.01) as number,
      selling_costs_pct: (sa.selling_costs_pct ?? pd.selling_costs_pct ?? 0.015) as number,
      renewal_probability_pct: (ra.renewal_probability_pct ?? 0.70) as number,
      downtime_months: (ra.downtime_months ?? 6) as number,
      lease_term_years: (ra.lease_term_years ?? 5) as number,
      new_tenant_ti_psf: (ra.new_tenant_ti_psf ?? 20) as number,
      renewal_tenant_ti_psf: (ra.renewal_tenant_ti_psf ?? 5) as number,
      // Deal-level date is the source of truth; scenario-level dates are a
      // legacy artifact and must not be echoed back into the deal patch.
      analysis_start_date: (pd.analysis_start_date ?? sa.analysis_start_date ?? "") as string,
      hold_period_years: (pd.hold_period_years ?? 10) as number,
      purchase_price: (pd.purchase_price ?? 0) as number,
      loan_amount: (debt.loan_amount ?? 0) as number,
      interest_rate: (debt.interest_rate ?? 0.05) as number,
      amortization_years: (debt.amortization_years ?? 25) as number,
      io_period_years: (debt.io_period_years ?? 0) as number,
      expense_growth_rates: (sa.expense_growth_rates ?? undefined) as Record<string, number> | undefined,
    });
  };

  const handleRecalculate = useCallback(async (a: Assumptions) => {
    if (!dealId || !selectedId) return;
    setRecalculating(true);
    try {
      // Patch deal-level assumptions FIRST (analysis_start_date, purchase
      // price, debt, ...) so the scenario re-run below projects against the
      // updated property data — the reverse order ran the scenario with the
      // stale start date.
      const dealPatch = buildDealAssumptionsPatch(a);
      if (Object.keys(dealPatch).length > 0) {
        await updateDealAssumptions(dealId, dealPatch);
      }
      const scenarioPayload = buildApiPayload(a);
      // Use update-and-run to save assumptions AND re-project in one call
      await updateAndRunScenario(dealId, selectedId, { assumptions: scenarioPayload });
      const [freshDeal, freshScenarios] = await Promise.all([
        (await import("../api/client")).fetchDeal(dealId),
        fetchScenarios(dealId),
      ]);
      setDeal(freshDeal);
      setScenarios(freshScenarios);
      addToast("Projection updated", "success");
    } catch (e: any) {
      addToast(friendlyError(e?.response?.data?.detail || "Couldn't recalculate. Try again."), "error");
    } finally {
      setRecalculating(false);
    }
  }, [dealId, selectedId, setDeal, addToast]);

  const scenario = scenarios.find((s) => s.id === selectedId);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (scenarios.length === 0) {
    return (
      <EmptyState
        icon={Calculator}
        title="No scenarios yet"
        description="Run a projection first to create a scenario, then adjust its assumptions here. Upload documents on the Documents tab to get started."
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-xl font-semibold text-bark-800">Assumptions</h2>
        <p className="text-sm text-bark-400 mt-1">
          Model inputs for the selected scenario. Changes recalculate the Pro Forma automatically.
        </p>
      </div>

      {scenarios.length > 1 && (
        <div className="flex gap-2">
          {scenarios.map((s) => (
            <button
              key={s.id}
              onClick={() => { setSelectedId(s.id); loadAssumptionsFromScenario(s); }}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                s.id === selectedId
                  ? "bg-forest-600 text-white"
                  : "text-bark-500 bg-white border border-grove-300 hover:bg-grove-50"
              }`}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}

      <AssumptionsPanel
        assumptions={assumptions}
        onAssumptionsChange={setAssumptions}
        onRecalculate={handleRecalculate}
        recalculating={recalculating}
        overrideKeys={scenario?.assumption_overrides ? new Set(Object.keys(scenario.assumption_overrides)) : undefined}
        vacancyExemptSummary={(() => {
          const tenants = (deal?.property_data as any)?.tenants || [];
          const exempt = tenants.filter((t: any) => t.vacancy_exempt);
          if (exempt.length === 0) return undefined;
          const exemptRent = exempt.reduce((s: number, t: any) =>
            s + (t.base_rent_annual_psf || 0) * (t.leased_sf || 0), 0);
          const totalPgi = tenants.reduce((s: number, t: any) =>
            s + (t.base_rent_annual_psf || 0) * (t.leased_sf || 0), 0);
          const pct = fmtPercent(totalPgi > 0 ? exemptRent / totalPgi : 0, 0);
          return `${exempt.length} tenant${exempt.length > 1 ? "s" : ""} excluded from vacancy (${fmtCurrency(exemptRent)} annual rent, ${pct} of PGI)`;
        })()}
      />
    </div>
  );
}
